import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import {
  Home as HomeIcon,
  Building2,
  BedDouble,
  UtensilsCrossed,
  Layers,
  Sofa,
  Droplets,
  HardHat,
  Building,
  Shield,
  Clock,
  Users,
  Leaf,
  MessageCircle,
  Star,
  Minus,
  Plus,
  Calendar,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { useServices } from "@/hooks/use-services";

const heroImage = "/hero-cleaning-cart.png";

export default function Home() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // Nothing is preselected — the user must choose a service type and fill in values.
  const [serviceType, setServiceType] = useState<"hourly" | "deep" | "chalet" | null>(null);
  const [workers, setWorkers] = useState<number | null>(null);
  const [deepProperty, setDeepProperty] = useState<"house" | "apartment" | null>(null);
  const [deepFloors, setDeepFloors] = useState<number | null>(null);
  const [deepRooms, setDeepRooms] = useState<number | null>(null);
  const [deepBathrooms, setDeepBathrooms] = useState<number | null>(null);
  const [deepLivingRooms, setDeepLivingRooms] = useState<number | null>(null);
  const [deepKitchens, setDeepKitchens] = useState<number | null>(null);
  const [deepFurnished, setDeepFurnished] = useState<boolean | null>(null);
  const [chaletRooms, setChaletRooms] = useState<number | null>(null);
  const [hours, setHours] = useState<number | null>(null);

  // Validation flags (the missing-fields memo is computed after the service
  // config is resolved, so it can enforce each service's min/max constraints).
  const [, setLocation] = useLocation();
  const [submitted, setSubmitted] = useState(false);

  // Resolve live service config to apply the same min/max bounds as the booking page
  const { data: services } = useServices();
  const hourlyService = useMemo(
    () => (services ?? []).find((s) => s.isActive && !s.isAddOn && s.pricingType === "hourly"),
    [services]
  );
  const deepHouseService = useMemo(
    () => (services ?? []).find((s) => s.isActive && !s.isAddOn && s.pricingType === "deep_house"),
    [services]
  );
  // Apartment pricing is a lookup table — without rows it can't price anything.
  const deepApartmentService = useMemo(
    () => (services ?? []).find((s) => s.isActive && !s.isAddOn && s.pricingType === "deep_apartment" && (s.apartmentPriceItems?.length ?? 0) > 0),
    [services]
  );
  const chaletService = useMemo(
    () => (services ?? []).find((s) => s.isActive && !s.isAddOn && s.pricingType === "chalet"),
    [services]
  );
  const maxChaletRooms = chaletService?.priceTiers
    ? Math.max(...chaletService.priceTiers.map((ti) => ti.maxRooms))
    : 10;

  // Validation: track which required fields are still missing, but only after
  // the user has attempted to confirm. Enforces each service's min/max bounds.
  const missing = useMemo(() => {
    const m = new Set<string>();
    if (!serviceType) {
      m.add("serviceType");
      return m; // nothing else is relevant until a type is chosen
    }
    if (serviceType === "hourly") {
      const minH = hourlyService?.minHours ?? 1;
      const minW = hourlyService?.minWorkers ?? 1;
      if (hours == null || hours < minH) m.add("hours");
      if (workers == null || workers < minW) m.add("workers");
    } else if (serviceType === "deep") {
      if (!deepProperty) {
        m.add("property");
      } else {
        if (deepProperty === "house" && (deepFloors == null || deepFloors < (deepHouseService?.minFloors ?? 1))) m.add("floors");
        if (deepProperty === "apartment") {
          // At least one unit must be selected (value >= 1).
          const unitsSelected = (deepRooms ?? 0) >= 1 || (deepBathrooms ?? 0) >= 1 || (deepLivingRooms ?? 0) >= 1 || (deepKitchens ?? 0) >= 1;
          if (!unitsSelected) {
            m.add("rooms");
            m.add("bathrooms");
            m.add("livingRooms");
            m.add("kitchens");
          }
          // Furnished/Unfurnished only appears once at least one unit is selected.
          if (unitsSelected && deepFurnished == null) m.add("furnished");
        }
      }
    } else if (serviceType === "chalet") {
      if (chaletRooms == null || chaletRooms < 1) m.add("chaletRooms");
    }
    return m;
  }, [serviceType, hours, workers, deepProperty, deepFloors, deepRooms, deepBathrooms, deepLivingRooms, deepKitchens, deepFurnished, chaletRooms, hourlyService, deepHouseService]);

  const hasErrors = missing.size > 0;
  // Show error styling only after a submit attempt, and only for fields still missing.
  const showError = (field: string) => submitted && missing.has(field);

  // Live price preview (same formulas as the booking page).
  // Returns null until the user has filled the values needed for the chosen service.
  const previewPrice = useMemo(() => {
    if (serviceType === "hourly" && hourlyService?.hourlyRate != null) {
      if (hours == null || workers == null) return null;
      return hourlyService.hourlyRate * hours * workers;
    }
    if (serviceType === "deep" && deepProperty === "house" && deepHouseService?.pricePerFloor != null) {
      if (deepFloors == null) return null;
      return deepHouseService.pricePerFloor * deepFloors;
    }
    if (serviceType === "deep" && deepProperty === "apartment" && deepApartmentService) {
      // Price shows once at least one unit (>=1) and the furnished type are selected.
      const unitsSelected = (deepRooms ?? 0) >= 1 || (deepBathrooms ?? 0) >= 1 || (deepLivingRooms ?? 0) >= 1 || (deepKitchens ?? 0) >= 1;
      if (!unitsSelected || deepFurnished == null) {
        return null;
      }
      // Exact-match lookup — combinations without a configured row show no price.
      const item = (deepApartmentService.apartmentPriceItems ?? []).find(
        (it) =>
          it.rooms === (deepRooms ?? 0) &&
          it.bathrooms === (deepBathrooms ?? 0) &&
          it.livingRooms === (deepLivingRooms ?? 0) &&
          it.kitchens === (deepKitchens ?? 0)
      );
      if (!item) return null;
      return deepFurnished ? item.furnished.price : item.unfurnished.price;
    }
    if (serviceType === "chalet" && chaletService?.priceTiers) {
      if (chaletRooms == null) return null;
      const tier = chaletService.priceTiers.find((ti) => chaletRooms >= ti.minRooms && chaletRooms <= ti.maxRooms);
      return tier?.price ?? 0;
    }
    return null;
  }, [serviceType, hourlyService, deepHouseService, deepApartmentService, chaletService, hours, workers, deepProperty, deepFloors, deepRooms, deepBathrooms, deepLivingRooms, deepKitchens, deepFurnished, chaletRooms]);

  const cur = t("services.currency");

  // Build booking URL carrying all selected values so the booking page pre-fills them.
  // Until the user picks a service type (and fills its values), we link to the bare /booking page.
  const bookingHref = useMemo(() => {
    if (!serviceType) return "/booking";
    const p = new URLSearchParams();
    p.set("type", serviceType);
    if (serviceType === "hourly") {
      if (hours != null) p.set("hours", String(hours));
      if (workers != null) p.set("workers", String(workers));
    } else if (serviceType === "deep") {
      if (deepProperty) p.set("property", deepProperty);
      if (deepFloors != null) p.set("floors", String(deepFloors));
      if (deepProperty === "apartment") {
        if (deepRooms != null) p.set("rooms", String(deepRooms));
        if (deepBathrooms != null) p.set("bathrooms", String(deepBathrooms));
        if (deepLivingRooms != null) p.set("livingRooms", String(deepLivingRooms));
        if (deepKitchens != null) p.set("kitchens", String(deepKitchens));
        if (deepFurnished != null) p.set("furnished", String(deepFurnished));
      }
    } else if (serviceType === "chalet") {
      if (chaletRooms != null) p.set("rooms", String(chaletRooms));
    }
    return `/booking?${p.toString()}`;
  }, [serviceType, hours, workers, deepProperty, deepFloors, deepRooms, deepBathrooms, deepLivingRooms, deepKitchens, deepFurnished, chaletRooms]);

  const heroFeatureBullets = [
    {
      icon: <HomeIcon className="w-5 h-5 text-white" />,
      bold: t("heroFeatures.bullet1Bold"),
      text: t("heroFeatures.bullet1Text"),
    },
    {
      icon: <Users className="w-5 h-5 text-white" />,
      bold: t("heroFeatures.bullet2Bold"),
      text: t("heroFeatures.bullet2Text"),
    },
  ];

  const trustBadges = [
    { icon: <Shield className="w-5 h-5 text-primary" />, label: t("heroFeatures.badge1") },
    { icon: <Clock className="w-5 h-5 text-primary" />, label: t("heroFeatures.badge2") },
    { icon: <Users className="w-5 h-5 text-primary" />, label: t("heroFeatures.badge3") },
    { icon: <Leaf className="w-5 h-5 text-primary" />, label: t("heroFeatures.badge4") },
  ];

  const servicesList = [
    { icon: <HomeIcon className="w-4 h-4" />, label: t("homeServices.villas") },
    { icon: <Building2 className="w-4 h-4" />, label: t("homeServices.offices") },
    { icon: <BedDouble className="w-4 h-4" />, label: t("homeServices.apartments") },
    { icon: <UtensilsCrossed className="w-4 h-4" />, label: t("homeServices.kitchens") },
    { icon: <Layers className="w-4 h-4" />, label: t("homeServices.carpets") },
    { icon: <Sofa className="w-4 h-4" />, label: t("homeServices.sofas") },
    { icon: <Droplets className="w-4 h-4" />, label: t("homeServices.tanks") },
    { icon: <HardHat className="w-4 h-4" />, label: t("homeServices.postConstruction") },
    { icon: <Building className="w-4 h-4" />, label: t("homeServices.facades") },
  ];

  const partnerApps = [
    { name: isRTL ? "غسيل" : "Ghaseel", icon: <Droplets className="w-5 h-5 text-blue-500" /> },
    { name: isRTL ? "يوركير" : "Yourcare", icon: <Star className="w-5 h-5 text-amber-500" /> },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">

      {/* ─── Hero ─── */}
      <section className="bg-background pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="container mx-auto px-6 sm:px-4">
          {/* dir="ltr" keeps text on left and image on right regardless of page RTL */}
          <div dir="ltr" className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-8 items-center">

            {/* Text column — restore RTL for Arabic */}
            <div dir={isRTL ? "rtl" : "ltr"} className="space-y-6 sm:space-y-7 text-start ps-4 lg:ps-0">
              <h1 className="font-display leading-tight">
                <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground">
                  {isRTL ? "حلول التنظيف" : "Cleaning Solutions"}
                </span>
                <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-secondary">
                  {isRTL ? "لحياة أفضل" : "For a Better Life"}
                </span>
              </h1>

              <div className="space-y-4">
                {heroFeatureBullets.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                      {item.icon}
                    </div>
                    <div className="text-start">
                      <p className="font-bold text-foreground">{item.bold}</p>
                      <p className="text-muted-foreground text-sm">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-5">
                {trustBadges.map((badge, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5 text-center w-16">
                    <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center">
                      {badge.icon}
                    </div>
                    <span className="text-[11px] text-muted-foreground font-medium leading-tight">
                      {badge.label}
                    </span>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setLocation("/booking")}
                className="inline-flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground font-bold text-sm px-7 py-3.5 hover:bg-secondary/90 transition-colors shadow-lg"
              >
                <Calendar className="w-4 h-4" />
                {t("hero.bookNow")}
              </button>

            </div>

            {/* Image column */}
            <div className="flex items-center justify-center">
              <img
                src={heroImage}
                alt="Cleaning service illustration"
                className="w-full max-w-xs sm:max-w-sm lg:max-w-lg drop-shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Three-column info section ─── */}
      <section className="py-12 bg-muted/30 border-y border-border/50">
        <div className="container mx-auto px-6 sm:px-4">
          <div className="grid lg:grid-cols-2 gap-8">

            {/* Column 1 — Services list */}
            <div>
              <h3 className="text-lg font-bold text-foreground mb-5 pb-3 border-b border-border">
                {t("homeServices.title")}
              </h3>
              <ul className="space-y-3">
                {servicesList.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="text-secondary flex-shrink-0">{item.icon}</span>
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 2 — About */}
            <div>
              <h3 className="text-lg font-bold text-foreground mb-5 pb-3 border-b border-border">
                {t("homeAbout.title")}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                {t("homeAbout.description")}
              </p>

              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                {t("homeAbout.licensedBy")}
              </p>
              <div className="flex items-center gap-3 p-3 rounded-xl border border-border bg-background mb-5">
                <img
                  src="/mocci-logo.png"
                  alt="Ministry of Commerce and Industry - Kuwait"
                  className="h-12 w-12 object-contain flex-shrink-0"
                />
                <p className="text-xs text-muted-foreground leading-snug whitespace-pre-line">
                  {isRTL
                    ? "وزارة التجارة والصناعة\nدولة الكويت"
                    : "Ministry of Commerce\nand Industry, Kuwait"}
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-24 bg-muted/50">
        <div className="container mx-auto px-6 sm:px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 font-display">
            {t("faq.title")}
          </h2>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {(
                t("faq.items", { returnObjects: true }) as {
                  question: string;
                  answer: string;
                }[]
              ).map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border-b border-border"
                >
                  <AccordionTrigger
                    className="text-start py-4 hover:no-underline"
                    data-testid={`faq-question-${index}`}
                  >
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}

// Compact stepper for the home booking widget (dark navy theme).
// value is null until the user picks a number (nothing preselected).
function WidgetStepper({
  label, value, min, max, onDecrement, onIncrement, testId, error,
}: {
  label: string;
  value: number | null;
  min: number;
  max: number;
  onDecrement: () => void;
  onIncrement: () => void;
  testId: string;
  error?: boolean;
}) {
  const atMin = value == null || value <= min;
  const atMax = value != null && value >= max;
  return (
    <div>
      <p className={cn("text-xs mb-2", error ? "text-red-400" : "text-slate-400")}>{label}</p>
      <div
        className={cn(
          "flex items-center justify-between rounded-lg px-3 py-2 border",
          error ? "bg-red-500/10 border-red-500" : "bg-[hsl(217,54%,24%)] border-transparent"
        )}
      >
        <button
          onClick={onDecrement}
          disabled={atMin}
          className={cn(
            "w-6 h-6 flex items-center justify-center",
            atMin ? "text-slate-600 cursor-not-allowed" : "text-slate-300 hover:text-white"
          )}
          data-testid={`${testId}-minus`}
        >
          <Minus className="w-4 h-4" />
        </button>
        <span className={cn("font-bold text-lg", error && "text-red-300")}>{value == null ? 0 : value}</span>
        <button
          onClick={onIncrement}
          disabled={atMax}
          className={cn(
            "w-6 h-6 flex items-center justify-center",
            atMax ? "text-slate-600 cursor-not-allowed" : "text-slate-300 hover:text-white"
          )}
          data-testid={`${testId}-plus`}
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

